/**
 * Atomic Data Filters Component
 *
 * Provides filtering controls for demo atomic shift-daily data:
 * - Date range picker (start/end dates)
 * - Location multi-select
 * - Shift filter (ALL, AM, PM, etc.)
 * - Apply/Reset buttons
 * - Validation for date ranges
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Alert,
  Chip,
  OutlinedInput,
  Divider
} from '@mui/material';
import { subDays, format, differenceInDays, parseISO } from 'date-fns';
import AdminButton from 'components/common/AdminButton';

// Shift options
const SHIFT_OPTIONS = [
  { value: 'ALL', label: 'All Shifts' },
  { value: 'BREAKFAST', label: 'Breakfast' },
  { value: 'LUNCH', label: 'Lunch' },
  { value: 'DINNER', label: 'Dinner' }
];

/**
 * Atomic Data Filters Component
 * @param {Object} props - Component props
 * @param {Array} props.locations - Array of location objects {id, name}
 * @param {Function} props.onApply - Callback when filters are applied
 * @param {Function} props.onReset - Callback when filters are reset
 * @param {boolean} props.loading - Loading state (disables controls)
 * @param {string} props.minDate - Minimum selectable date (earliest data in DB)
 * @param {string} props.maxDate - Maximum selectable date (latest data in DB)
 */
const AtomicDataFilters = ({ locations = [], onApply, onReset, loading = false, minDate = null, maxDate = null }) => {
  // Calculate default dates based on data bounds or fallback to last 7 days
  const getDefaultDates = (minBound, maxBound) => {
    if (maxBound) {
      // Use latest data date as end date
      const endDateValue = maxBound;
      const endDateObj = parseISO(maxBound);
      const sixDaysAgo = subDays(endDateObj, 6);

      // Start date is 6 days before end date, or min date if that's earlier
      let startDateValue;
      if (minBound && parseISO(minBound) > sixDaysAgo) {
        startDateValue = minBound;
      } else {
        startDateValue = format(sixDaysAgo, 'yyyy-MM-dd');
      }

      return {
        startDate: startDateValue,
        endDate: endDateValue
      };
    }

    // Fallback to last 7 days from today
    const today = new Date();
    const sevenDaysAgo = subDays(today, 7);
    return {
      startDate: format(sevenDaysAgo, 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd')
    };
  };

  const defaults = getDefaultDates(minDate, maxDate);

  // Filter state
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedLocations, setSelectedLocations] = useState([]); // Empty = all locations
  const [selectedShift, setSelectedShift] = useState('ALL');

  // Update dates when bounds change (e.g., when client changes)
  useEffect(() => {
    const newDefaults = getDefaultDates(minDate, maxDate);
    setStartDate(newDefaults.startDate);
    setEndDate(newDefaults.endDate);
  }, [minDate, maxDate]);

  // Validation state
  const [validationError, setValidationError] = useState(null);
  const [warning, setWarning] = useState(null);

  // Validate date range whenever dates change
  useEffect(() => {
    if (!startDate || !endDate) {
      setValidationError('Both start and end dates are required');
      setWarning(null);
      return;
    }

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    // Check if start is before end
    if (start > end) {
      setValidationError('Start date must be before end date');
      setWarning(null);
      return;
    }

    // Check for max 90 days
    const daysDiff = differenceInDays(end, start);
    if (daysDiff > 90) {
      setValidationError('Date range cannot exceed 90 days');
      setWarning(null);
      return;
    }

    // Clear validation error
    setValidationError(null);

    // Show warning for >30 days
    if (daysDiff > 30) {
      setWarning('Large date range may result in slower query performance');
    } else {
      setWarning(null);
    }
  }, [startDate, endDate]);

  /**
   * Handle Apply button click
   */
  const handleApply = () => {
    if (validationError) return;

    const filters = {
      startDate,
      endDate,
      locationIds: selectedLocations.length > 0 ? selectedLocations : null, // null = all
      shift: selectedShift === 'ALL' ? null : selectedShift // null = all
    };

    onApply(filters);
  };

  /**
   * Handle Reset button click
   */
  const handleReset = () => {
    const defaults = getDefaultDates(minDate, maxDate);
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
    setSelectedLocations([]);
    setSelectedShift('ALL');
    setValidationError(null);
    setWarning(null);

    // Call parent reset callback
    if (onReset) {
      onReset();
    }
  };

  /**
   * Handle location selection change
   */
  const handleLocationChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedLocations(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  /**
   * Select all locations
   */
  const handleSelectAllLocations = () => {
    setSelectedLocations(locations.map(loc => loc.id));
  };

  /**
   * Clear all location selections
   */
  const handleClearAllLocations = () => {
    setSelectedLocations([]);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Filter Controls */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
        {/* Start Date */}
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: minDate || undefined,
            max: maxDate || undefined
          }}
          disabled={loading}
          sx={{
            '& .MuiInputBase-root': { height: '40px' },
            '& .MuiInputLabel-root': { color: '#7C3AED' },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#7C3AED'
              }
            }
          }}
        />

        {/* End Date */}
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: minDate || undefined,
            max: maxDate || undefined
          }}
          disabled={loading}
          sx={{
            '& .MuiInputBase-root': { height: '40px' },
            '& .MuiInputLabel-root': { color: '#7C3AED' },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: '#7C3AED'
              }
            }
          }}
        />

        {/* Location Multi-Select */}
        <FormControl
          disabled={loading}
          sx={{
            '& .MuiInputBase-root': { height: '40px' },
            '& .MuiInputLabel-root': { color: '#7C3AED' },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7C3AED'
              }
            }
          }}
        >
          <InputLabel>Locations</InputLabel>
          <Select
            multiple
            value={selectedLocations}
            onChange={handleLocationChange}
            input={<OutlinedInput label="Locations" />}
            renderValue={(selected) =>
              selected.length === 0
                ? 'All Locations'
                : `${selected.length} selected`
            }
          >
            {/* Select All / Clear All actions */}
            <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1 }}>
              <AdminButton
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAllLocations();
                }}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1,
                  minWidth: 'auto',
                  backgroundColor: 'transparent',
                  color: '#7C3AED',
                  borderColor: '#7C3AED',
                  '&:hover': {
                    backgroundColor: 'rgba(124, 58, 237, 0.04)',
                    borderColor: '#7C3AED'
                  }
                }}
              >
                Select All
              </AdminButton>
              <AdminButton
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAllLocations();
                }}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1,
                  minWidth: 'auto',
                  backgroundColor: 'transparent',
                  color: '#7C3AED',
                  borderColor: '#7C3AED',
                  '&:hover': {
                    backgroundColor: 'rgba(124, 58, 237, 0.04)',
                    borderColor: '#7C3AED'
                  }
                }}
              >
                Clear All
              </AdminButton>
            </Box>
            <Divider />
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                <Checkbox checked={selectedLocations.indexOf(location.id) > -1} />
                <ListItemText primary={location.name || location.id} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Shift Filter */}
        <FormControl
          disabled={loading}
          sx={{
            '& .MuiInputBase-root': { height: '40px' },
            '& .MuiInputLabel-root': { color: '#7C3AED' },
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7C3AED'
              }
            }
          }}
        >
          <InputLabel>Shift</InputLabel>
          <Select
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            label="Shift"
          >
            {SHIFT_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Filter Summary Chips */}
      {(selectedLocations.length > 0 || selectedShift !== 'ALL') && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {selectedLocations.length > 0 && (
            <Chip
              label={`${selectedLocations.length} location(s) selected`}
              size="small"
              sx={{ backgroundColor: '#7C3AED', color: 'white' }}
            />
          )}
          {selectedShift !== 'ALL' && (
            <Chip
              label={`${selectedShift} shift only`}
              size="small"
              sx={{ backgroundColor: '#7C3AED', color: 'white' }}
            />
          )}
        </Box>
      )}

      {/* Validation Error */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* Warning */}
      {warning && !validationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <AdminButton
          variant="contained"
          onClick={handleApply}
          disabled={loading || !!validationError}
          sx={{ height: '40px', minWidth: '120px' }}
        >
          Fetch Data
        </AdminButton>
        <AdminButton
          variant="outlined"
          onClick={handleReset}
          disabled={loading}
          sx={{
            height: '40px',
            minWidth: '120px',
            backgroundColor: 'transparent',
            color: '#7C3AED',
            borderColor: '#7C3AED',
            '&:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.04)',
              borderColor: '#7C3AED'
            }
          }}
        >
          Reset
        </AdminButton>
      </Box>
    </Box>
  );
};

export default AtomicDataFilters;
